import { ERROR_MESSAGES } from 'apps/web/constants/errorMessages';
import { NO_CREDIT_STATUS_CODE } from 'libs/constants/src/lib/credit';
import { NO_CREDIT_MESSAGE } from 'libs/constants/src/lib/credit';

import { ErrorCode } from '@lessonPlanner/enums';
interface ErrorWithCode {
  name?: string;
  code?: string;
  message: string;
  statusCode?: number;
  stack?: string;
}

interface ErrorDetails {
  message: string;
  code?: string;
  statusCode?: number;
  raw?: string;
}

interface ErrorModalDetailsProps {
  error: ErrorDetails;
}

function getErrorMessage(error: ErrorWithCode): ErrorDetails {
  let message = ERROR_MESSAGES[ErrorCode.UNKNOWN_ERROR];
  let errorCode = ErrorCode.UNKNOWN_ERROR;
  const rawMessage = error.message;
  const statusCode = error.statusCode;

  // Check if error code is already provided in a structured format
  if (error.code && error.code.startsWith('Error: ')) {
    const code = error.code.substring(7);
    const matchingCode = Object.values(ErrorCode).find((ec) => ec === code);
    if (matchingCode && ERROR_MESSAGES[matchingCode]) {
      return {
        message: ERROR_MESSAGES[matchingCode],
        code: error.code,
        statusCode,
        raw: rawMessage,
      };
    }
  }

  const isRateLimitError =
    error.message.toLowerCase().includes('too many requests') ||
    error.statusCode === 429 ||
    (statusCode === 500 &&
      error.message === 'Internal server error' &&
      error.stack &&
      error.stack.includes('Credit.ts'));

  const isServerCreditError =
    statusCode === 500 &&
    (error.message.includes('429') ||
      error.message.includes('too many requests'));

  const promptNotFoundError = error.message
    .toLowerCase()
    .includes('prompt not found');

  const isUserCreditError =
    statusCode === NO_CREDIT_STATUS_CODE ||
    error.message === NO_CREDIT_MESSAGE ||
    error.message.toLowerCase().includes('not enough credits') ||
    (error.stack && error.stack.includes('Module.getUserCredit'));

  const isInvalidInputError =
    statusCode === 400 && error.message.toLowerCase().includes('invalid input');

  const isServerError = statusCode >= 500 && statusCode < 600;

  const isApiKeyError =
    error.message &&
    (error.message.includes('401') ||
      error.message.includes('api key') ||
      error.message.includes('authenticate'));

  const isNetworkError =
    error.message.includes('Failed to fetch') ||
    error.message?.toLowerCase().includes('network') ||
    !navigator.onLine;

  if (isRateLimitError) {
    message = ERROR_MESSAGES[ErrorCode.RATE_LIMIT];
    errorCode = ErrorCode.RATE_LIMIT;
  } else if (isServerCreditError) {
    message = ERROR_MESSAGES[ErrorCode.PROVIDER_ERROR];
    errorCode = ErrorCode.PROVIDER_ERROR;
  } else if (promptNotFoundError) {
    message = ERROR_MESSAGES[ErrorCode.PROMPT_NOT_FOUND];
    errorCode = ErrorCode.PROMPT_NOT_FOUND;
  } else if (isUserCreditError) {
    message = ERROR_MESSAGES[ErrorCode.INSUFFICIENT_CREDITS];
    errorCode = ErrorCode.INSUFFICIENT_CREDITS;
  } else if (isInvalidInputError) {
    message = ERROR_MESSAGES[ErrorCode.INVALID_INPUT];
    errorCode = ErrorCode.INVALID_INPUT;
  }
  // D2 & D3: Server errors (50x)
  else if (isServerError) {
    // Try to detect D3 API key errors if possible
    if (isApiKeyError) {
      message = ERROR_MESSAGES[ErrorCode.API_KEY_ERROR];
      errorCode = ErrorCode.API_KEY_ERROR;
    }
    // Default to general server error
    else {
      message = ERROR_MESSAGES[ErrorCode.SERVER_MODEL_ERROR];
      errorCode = ErrorCode.SERVER_MODEL_ERROR;
    }
  }
  // F1: Network errors
  else if (isNetworkError) {
    message = ERROR_MESSAGES[ErrorCode.NETWORK_ERROR];
    errorCode = ErrorCode.NETWORK_ERROR;
  }

  return {
    message,
    code: `${errorCode}`,
    statusCode,
    raw: rawMessage,
  };
}

export function ErrorModalDetails({ error }: ErrorModalDetailsProps) {
  return (
    <div className="space-y-2">
      <p>{error.message}</p>
      <div className="flex flex-col">
        <span className="text-semibold text-xs leading-tight sm:leading-normal">
          {error.code && <span className="text-gray-500">{error.code}</span>}
          {error.code && ' • '}
          Please screenshot this error and report to the Lesson Planner team
        </span>
      </div>
    </div>
  );
}

export { getErrorMessage };
export type { ErrorDetails, ErrorWithCode };
